import { useIntl } from 'react-intl';
import { Selector } from 'sonata-react-components';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';

export type DeviatedLineItemStatus = 'active' | 'paused' | 'finished';

// Applied when the user has not picked any status (untouched or cleared), incl. after "Reset filters".
export const DEFAULT_DEVIATED_LINE_ITEM_STATUS: DeviatedLineItemStatus[] = ['active'];

const STATUSES: DeviatedLineItemStatus[] = ['active', 'paused', 'finished'];

type FilterState = { id: string; value: string[] | undefined }[];

type Props = {
  id: string;
  onFilterStatus?: (state: FilterState) => FilterState;
  value?: DeviatedLineItemStatus[];
};

export default function StatusSelector({ id, onFilterStatus, value }: Props) {
  const { formatMessage } = useIntl();

  const labels: Record<DeviatedLineItemStatus, string> = {
    active: formatMessage({ defaultMessage: 'Active', id: 'deviatedLineItems.filters.status.active' }),
    finished: formatMessage({ defaultMessage: 'Finished', id: 'deviatedLineItems.filters.status.finished' }),
    paused: formatMessage({ defaultMessage: 'Paused', id: 'deviatedLineItems.filters.status.paused' })
  };

  const options: SelectorOptionPropsInterface[] = STATUSES.map(status => ({ id: status, label: labels[status] }));

  const selectedValue = options.filter(({ id: optionId }) =>
    (value?.length ? value : DEFAULT_DEVIATED_LINE_ITEM_STATUS).includes(optionId as DeviatedLineItemStatus)
  );

  const handleChange = (changedSelectedOptions: SelectorOptionPropsInterface[]) => {
    onFilterStatus?.([
      {
        id: 'status',
        value: changedSelectedOptions.length
          ? changedSelectedOptions.map(({ id: selectedOptionId }) => selectedOptionId.toString())
          : undefined
      }
    ]);
  };

  return (
    <Selector<SelectorOptionPropsInterface[]>
      id={`${id}_selector`}
      label={formatMessage({ defaultMessage: 'Status', id: 'deviatedLineItems.filters.status.label' })}
      onChange={handleChange}
      options={options}
      showSelectedItems={false}
      type="multiple"
      value={selectedValue}
    />
  );
}
